// client/pages/Inventar.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../constants/api";
import { showError, showForbidden, showSuccess } from "../utils/toast.js";
import { INVENTAR_FELDER } from "../constants/inventarKategorien";
import { INVENTAR_STATUS_OPTIONS } from "../constants/inventarStatus";
import InventarStatusBadge from "../components/Inventar/InventarStatusBadge";
import { smartBack } from "../constants/nav";
import useSmartSearch from "../src/hooks/useSmartSearch";
import { addSynonyms } from "../src/search/synonyms";
import { useAuth } from "../components/ui/AuthContext";
import { useTheme } from "../context/ThemeContext.jsx";
import { usePreferences } from "../context/PreferencesContext.jsx";
import ResponsiveDataView from "../components/ui/ResponsiveDataView.jsx";
import FilterBarOrDrawer from "../components/ui/FilterBarOrDrawer.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import { useConfirm } from "../components/ui/ConfirmationModal.jsx";
import {
  detailPageHeaderNavClassName,
  detailPageHeaderNavTouchClassName,
  detailPageHeaderNavActionsClassName,
  detailPageHeaderNavPrimaryMobileClassName,
  destructiveTonalInteractiveClassName,
  entityRowDeleteActionClassName,
  entityRowTonalActionClassName,
  listSortTriggerClassName,
  segmentTabChipButtonClassName,
} from "../design/tokens.js";
import { useResponsive } from "../hooks/useResponsive.js";
import { cn } from "../utils/themeClasses.js";
import { emojiForProjektTyp, labelForProjektTyp } from "../constants/projektTypVisuals.js";
import { Input } from "../components/forms/shared/FormAtoms.jsx";
import StatusFilter from "../components/ui/StatusFilter.jsx";
import SortButton from "../components/ui/SortButton.jsx";
import { Z_INDEX } from "../components/ui/zIndex.js";
// ✅ Enterprise List Controller
import useEnterpriseListController from "../src/hooks/useEnterpriseListController";
import useListPaginationUi from "../src/hooks/useListPaginationUi";
// 🏢 Enterprise: Zentrale Pagination-Limits
import { DEFAULT_PAGE_SIZE } from "../constants/pagination";
import { canEnterHoursUnlimited, getRoleId, ROLE_MITARBEITER } from "../constants/roles.js";

const resolveNextValue = (prev, nextOrUpdater) =>
  typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;

const INVENTAR_SORT_OPTIONS = [
  { key: "neueste", label: "Neueste zuerst", fixedDir: "desc" },
  { key: "bezeichnung", label: "Bezeichnung", section: "ALLGEMEIN" },
  { key: "typ", label: "Typ", section: "ALLGEMEIN" },
  { key: "kategorie", label: "Kategorie", section: "ALLGEMEIN" },
  { key: "standort", label: "Standort", section: "ALLGEMEIN" },
  { key: "status", label: "Status", section: "PROZESS" },
  { key: "menge", label: "Menge", section: "PROZESS", fixedDir: "desc" },
];

function inventarSortDefaultDir(key) {
  const k = String(key || "").toLowerCase();
  if (k === "neueste" || k === "menge") return "desc";
  return "asc";
}

function FilterMenuItem({ active, label, onClick }) {
  return (
    <AppButton
      type="button"
      variant="ghost"
      role="option"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "w-full text-left px-3 py-2 rounded-lg text-sm transition min-h-0 !justify-start",
        active ? "bg-[#0e1a34]/70 text-blue-100 border border-white/10" : "text-blue-100/90 hover:bg-white/10"
      )}
    >
      {label}
    </AppButton>
  );
}

function InventarFilterPopover({ pos, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const onClick = (e) => {
      if (!ref.current?.contains(e.target)) onClose?.();
    };
    const onRecalc = () => onClose?.();
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onClick);
    window.addEventListener("resize", onRecalc, { passive: true });
    window.addEventListener("scroll", onRecalc, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onClick);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div ref={ref} style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: Z_INDEX.dropdown }}>
      <div className="w-full rounded-xl popover-panel backdrop-blur-md p-1 transition-all" role="menu">
        {children}
      </div>
    </div>,
    document.body
  );
}

function InventarKategorieFilter({ value, onChange, options = [], triggerClassName }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240 });

  const currentLabel = useMemo(() => {
    if (!value || value === "__all__") return "Alle Kategorien";
    return String(value);
  }, [value]);

  const openMenu = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuWidth = 240;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const left = Math.max(8, Math.min(r.left, vw - menuWidth - 8));
    setPos({ top: r.bottom + 8, left, width: menuWidth });
    setOpen(true);
  };

  return (
    <>
      <AppButton
        ref={btnRef}
        type="button"
        variant="secondary"
        onClick={openMenu}
        className={cn("font-normal whitespace-nowrap", triggerClassName ?? listSortTriggerClassName)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Kategorie filtern"
      >
        {currentLabel}
      </AppButton>
      {open && (
        <InventarFilterPopover pos={pos} onClose={() => setOpen(false)}>
          <div role="listbox" aria-label="Kategorie wählen" className="max-h-[60vh] overflow-auto">
            <FilterMenuItem active={value === "__all__"} label="Alle Kategorien" onClick={() => { onChange("__all__"); setOpen(false); }} />
            {options.length > 0 && <div className="my-1 h-px bg-border-muted" />}
            {options.map((opt) => (
              <FilterMenuItem
                key={opt}
                active={String(value) === String(opt)}
                label={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
              />
            ))}
          </div>
        </InventarFilterPopover>
      )}
    </>
  );
}

/* ─────────────────────────── Seite ─────────────────────────── */
export default function Inventar() {
  const { isDark } = useTheme();
  const { isDesktopXl } = useResponsive();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getPref, setPref } = usePreferences();
  const confirm = useConfirm();
  const roleId = getRoleId(user);
  const canWrite = canEnterHoursUnlimited(user) || roleId === ROLE_MITARBEITER;
  const canDelete = canEnterHoursUnlimited(user);

  useEffect(() => {
    document.title = "Inventar • WireTrack";
  }, []);

  // Synonyme nur einmal registrieren
  useEffect(() => {
    addSynonyms({
      akku: ["batterie", "akkupack", "akkumulator"],
      bohrer: ["bohrmaschine", "bohrschrauber", "schlagbohrer", "hammerbohrer"],
      schleifer: ["winkelschleifer", "flex", "trennschleifer"],
      staubsauger: ["sauger", "bausauger", "industriesauger"],
      koffer: ["werkzeugkoffer", "box", "case"],
      tester: ["prüfgerät", "pruefgeraet", "messgerät", "messgeraet", "multimeter", "installationstester"],
      kabel: ["leitung", "draht"],
      steckdose: ["dose", "t13", "t23", "schuko"],
      verteiler: ["uv", "hv", "kv", "schaltschrank"],
      verfuegbar: ["frei", "lagernd", "auf lager", "vorhanden"],
      defekt: ["kaputt", "fehlerhaft", "beschädigt", "beschaedigt"],
      verliehen: ["ausgeliehen", "ausgabe", "ausgegeben", "im einsatz"],
      lager: ["magazin", "depot"],
      bus: ["fahrzeug", "auto", "transporter"],
    });
  }, []);

  /* Persist pro User (für Enterprise List Controller storageKey) */
  const [userId] = useState(() => {
    try {
      return Number(JSON.parse(sessionStorage.getItem("user") || "{}")?.id) || null;
    } catch {
      return null;
    }
  });

  /* Tabs */
  const TYPEN = useMemo(() => ["Alle", ...Object.keys(INVENTAR_FELDER)], []);
  const [tab, setTabRaw] = useState(() => getPref("tabs.inventar") || "Alle");
  const setTab = (v) => {
    setTabRaw(v);
    setPref("tabs.inventar", v);
  };

  /* Filter (Kategorie + Status) */
  const [filters, setFiltersRaw] = useState(() => {
    const s = getPref("filters.inventar");
    return { kat: s?.kat ?? "__all__", statusF: s?.statusF ?? "__all__" };
  });
  const setFilters = useCallback((nextOrUpdater) => {
    setFiltersRaw((prev) => {
      const next = resolveNextValue(prev, nextOrUpdater);
      setPref("filters.inventar", next);
      return next;
    });
  }, [setPref]);
  const kat = filters.kat;
  const statusF = filters.statusF;
  const setKat = (v) => setFilters((prev) => ({ ...prev, kat: v }));
  const setStatusF = (v) => setFilters((prev) => ({ ...prev, statusF: v }));
  useEffect(() => {
    setFilters((prev) => ({ ...prev, kat: "__all__" }));
  }, [tab]);

  const location = useLocation();
  
  /* ---------- Enterprise List Controller ---------- */
  const {
    items: apiItems,
    loading,
    error: apiError,
    search,
    setSearch,
    debouncedSearch,
    setFilter,
    sort: apiSort,
    setSortKey,
    toggleSortDir,
    refetch,
    total,
    pagination,
    setPage,
    pages,
  } = useEnterpriseListController({
    fetchFn: async (queryParams, signal) => {
      const res = await api.get(`/inventar`, {
        params: {
          q: queryParams.q,
          status: queryParams.status,
          kategorie: queryParams.kategorie,
          sort: queryParams.sort,
          dir: queryParams.dir,
          page: queryParams.page,
          pageSize: queryParams.pageSize,
        },
        signal, // ✅ Race-Condition-Prävention
      });
      const payload = res?.data?.data || res?.data || {};
      
      return {
        items: Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload) ? payload : []),
        total: payload?.total || payload?.items?.length || 0,
        page: payload?.page || queryParams.page || 1,
        pageSize: payload?.pageSize || queryParams.pageSize || DEFAULT_PAGE_SIZE,
      };
    },
    searchConfig: {
      debounceMs: 250,
      urlSync: true,
      storageKey: `inventar_q_${userId || 'anon'}`,
    },
    filterConfig: {
      filters: { status: '__all__', kategorie: '__all__' },
      urlSync: true,
      storageKey: `inventar_filters_${userId || 'anon'}`,
    },
    sortConfig: {
      defaultKey: 'id',
      defaultDir: 'desc',
      allowedKeys: ['id', 'bezeichnung', 'typ', 'kategorie', 'standort', 'status', 'menge'],
      urlSync: true,
      storageKey: `inventar_sort_${userId || 'anon'}`,
      defaultDirFor: (key) => {
        if (key === 'id' || key === 'menge') return 'desc';
        return 'asc';
      },
    },
    paginationConfig: {
      defaultPageSize: DEFAULT_PAGE_SIZE,
      urlSync: true,
    },
    urlSyncConfig: {
      enabled: true,
      pathname: location.pathname,
    },
    cacheConfig: { domain: "inventar" },
    rememberListReturn: true,
  });

  // Sync Filter mit Enterprise Controller
  useEffect(() => {
    setFilter('status', statusF);
  }, [statusF, setFilter]);
  
  useEffect(() => {
    setFilter('kategorie', kat);
  }, [kat, setFilter]);

  // Sort für komplexe Sortierungen (neueste bleibt client-side)
  const [complexSort, setComplexSortRaw] = useState(() => getPref("sort.inventar") || { key: "neueste", dir: "desc" });
  const setComplexSort = useCallback((nextOrUpdater) => {
    setComplexSortRaw((prev) => {
      const next = resolveNextValue(prev, nextOrUpdater);
      setPref("sort.inventar", next);
      return next;
    });
  }, [setPref]);

  const sort = complexSort.key === 'neueste'
    ? complexSort
    : apiSort;

  const setSort = (newSort) => {
    if (newSort.key === 'neueste') {
      setComplexSort(newSort);
    } else {
      setSortKey(newSort.key);
      if (newSort.dir !== apiSort.dir) {
        toggleSortDir();
      }
    }
  };
  
  // Items aus Enterprise Controller verwenden
  const items = apiItems;
  const err = apiError;
  const [savingId, setSavingId] = useState(null);
  const toMenge = useCallback((value) => {
    const n = Number(String(value ?? "").replace(",", "."));
    if (!Number.isFinite(n) || n < 0) return 0;
    return Number(n.toFixed(2));
  }, []);
  
  /* Menge patchen (optimistisch) */
  const patchMenge = useCallback(async (row, neueMenge) => {
    if (!canWrite) {
      showForbidden("restricted_role", { level: "error" });
      return;
    }
    const id = row.id;
    setSavingId(id);
    try {
      await api.patch(`/inventar/${id}`, { menge: neueMenge });
      refetch(); // ✅ Enterprise: Refetch nach Update
    } catch (e) {
      console.error(e);
    } finally {
      setSavingId(null);
    }
  }, [canWrite, refetch]);

  const handleDeleteRow = useCallback(
    async (row) => {
      if (!canDelete || !row?.id) return;
      const label = row.bezeichnung?.trim() || `Artikel #${row.id}`;
      const { ok } = await confirm({
        title: "Artikel löschen",
        message: `«${label}» wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
        variant: "danger",
        confirmLabel: "Löschen",
      });
      if (!ok) return;
      try {
        await api.delete(`/inventar/${row.id}`);
        showSuccess("In Papierkorb verschoben");
        refetch();
      } catch (e) {
        console.error(e);
        showError("Löschen fehlgeschlagen");
      }
    },
    [canDelete, confirm, refetch]
  );

  /* abgeleitete Daten */
  const kategorienOpts = useMemo(() => {
    const t = tab === "Alle" ? null : tab;
    if (!t) return [];
    return Object.keys(INVENTAR_FELDER[t] || {});
  }, [tab]);

  const baseFiltered = useMemo(() => {
    return (items || []).filter((row) => {
      if (tab !== "Alle" && String(row.projekttyp || "") !== tab) return false;
      if (kat !== "__all__" && String(row.kategorie || "") !== kat) return false;
      if (statusF !== "__all__" && String(row.status || "") !== statusF) return false;
      return true;
    });
  }, [items, tab, kat, statusF]);

  const searchCtx = useMemo(
    () => ({
      buildKeys: (row) => {
        const fullStandort = [row.standort_typ, row.standort_bez].filter(Boolean).join(" ");
        return [
          row.bezeichnung,
          row.projekttyp,
          row.kategorie,
          row.status,
          row.hersteller,
          row.seriennummer,
          fullStandort,
          String(row.id ?? ""),
        ];
      },
      buildDates: () => [],
    }),
    []
  );

  const searched = useSmartSearch(baseFiltered, debouncedSearch, searchCtx);

  const sorted = useMemo(() => {
    const arr = [...searched];
    const cmpTxt = (a, b, dir = "asc") => {
      const r = String(a || "").localeCompare(String(b || ""), "de");
      return dir === "asc" ? r : -r;
    };
    const cmpNum = (a, b, dir = "asc") => {
      const av = Number(a ?? 0);
      const bv = Number(b ?? 0);
      const r = av === bv ? 0 : av < bv ? -1 : 1;
      return dir === "asc" ? r : -r;
    };

    if (sort.key === "neueste") {
      arr.sort((a, b) => (sort.dir === "asc" ? a.id - b.id : b.id - a.id));
    } else if (sort.key === "bezeichnung") {
      arr.sort((a, b) => cmpTxt(a.bezeichnung, b.bezeichnung, sort.dir));
    } else if (sort.key === "typ") {
      arr.sort((a, b) => cmpTxt(a.projekttyp, b.projekttyp, sort.dir));
    } else if (sort.key === "kategorie") {
      arr.sort((a, b) => cmpTxt(a.kategorie, b.kategorie, sort.dir));
    } else if (sort.key === "standort") {
      arr.sort((a, b) =>
        cmpTxt(
          `${a.standort_typ || ""} ${a.standort_bez || ""}`,
          `${b.standort_typ || ""} ${b.standort_bez || ""}`,
          sort.dir
        )
      );
    } else if (sort.key === "status") {
      arr.sort((a, b) => cmpTxt(a.status, b.status, sort.dir));
    } else if (sort.key === "menge") {
      arr.sort((a, b) => cmpNum(a.menge, b.menge, sort.dir));
    }
    return arr;
  }, [searched, sort]);

  const tabLabel = (t) => (t === "Alle" ? "Alle" : labelForProjektTyp(String(t).toLowerCase()));
  const inventarSuffix = [
    tab !== "Alle" ? tabLabel(tab) : "",
    tab !== "Alle" && kat !== "__all__" ? kat : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const { summary: listSummary, footer: paginationFooter } = useListPaginationUi({
    loading,
    total,
    pagination,
    pages,
    setPage,
    debouncedSearch,
    suffix: inventarSuffix,
  });

  /* Desktop Table Editing State */
  const [tableEditingId, setTableEditingId] = useState(null);
  const [tableDraft, setTableDraft] = useState(0);

  const startTableEdit = useCallback((row) => {
    setTableEditingId(row.id);
    setTableDraft(Number(row.menge ?? 0));
  }, []);

  const cancelTableEdit = useCallback(() => {
    setTableEditingId(null);
  }, []);

  const saveTableEdit = useCallback(
    async (row) => {
      await patchMenge(row, toMenge(tableDraft));
      setTableEditingId(null);
    },
    [patchMenge, tableDraft, toMenge]
  );

  const decTable = useCallback(() => setTableDraft((v) => Math.max(0, Number((Number(v || 0) - 1).toFixed(2)))), []);
  const incTable = useCallback(() => setTableDraft((v) => Math.max(0, Number((Number(v || 0) + 1).toFixed(2)))), []);

  /* Spalten-Definition für ResponsiveDataView */
  const columns = useMemo(
    () => [
      {
        key: "bezeichnung",
        label: "Bezeichnung",
        render: (row) => (
          <span className="block max-w-[18rem] truncate" title={row.bezeichnung || "—"}>
            {row.bezeichnung || "—"}
          </span>
        ),
      },
      { key: "projekttyp", label: "Typ" },
      { key: "kategorie", label: "Kategorie" },
      { key: "hersteller", label: "Hersteller" },
      {
        key: "standort",
        label: "Standort",
        render: (row) =>
          `${row.standort_typ || "—"}${row.standort_bez ? ` – ${row.standort_bez}` : ""}`,
      },
      {
        key: "status",
        label: "Status",
        render: (row) => <InventarStatusBadge status={row.status || "verfügbar"} />,
      },
      {
        key: "menge",
        label: "Menge",
        align: "right",
        render: (row) => {
          const isEditing = tableEditingId === row.id;
          if (isEditing) {
            return (
              <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <AppButton
                  type="button"
                  variant="secondary"
                  className={`!p-1 text-xs min-h-[32px] min-w-[32px] ${
                    isDark
                      ? "!border-white/15 !bg-white/10 hover:!bg-white/15 !text-blue-100"
                      : "!border-slate-300/50 !bg-white hover:!bg-slate-50 !text-slate-700 shadow-sm"
                  }`}
                  onClick={decTable}
                  disabled={savingId === row.id}
                  aria-label="Menge verringern"
                >
                  −
                </AppButton>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  className={`!w-16 !min-h-0 !py-1 !px-2 !text-xs text-center ${
                    isDark ? "" : "shadow-sm"
                  }`}
                  value={tableDraft}
                  onChange={(e) => setTableDraft(e.target.value)}
                />
                <AppButton
                  type="button"
                  variant="secondary"
                  className={`!p-1 text-xs min-h-[32px] min-w-[32px] ${
                    isDark
                      ? "!border-white/15 !bg-white/10 hover:!bg-white/15 !text-blue-100"
                      : "!border-slate-300/50 !bg-white hover:!bg-slate-50 !text-slate-700 shadow-sm"
                  }`}
                  onClick={incTable}
                  disabled={savingId === row.id}
                  aria-label="Menge erhöhen"
                >
                  +
                </AppButton>
              </div>
            );
          }
          return <span className="tabular-nums">{Number(row.menge ?? 0)}</span>;
        },
      },
      {
        key: "aktion",
        label: "Aktion",
        align: "right",
        render: (row) => {
          const isEditing = tableEditingId === row.id;
          return (
            <div className="flex flex-nowrap items-center justify-end gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
              {isEditing ? (
                <>
                  <AppButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => saveTableEdit(row)}
                    className={entityRowTonalActionClassName}
                  >
                    Speichern
                  </AppButton>
                  <AppButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={cancelTableEdit}
                    className={entityRowTonalActionClassName}
                  >
                    Abbrechen
                  </AppButton>
                </>
              ) : (
                <>
                  {canWrite ? (
                    <AppButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => startTableEdit(row)}
                      className={entityRowTonalActionClassName}
                      title="Menge bearbeiten"
                    >
                      Bearbeiten
                    </AppButton>
                  ) : (
                    <span className="text-[11px] text-blue-200/60 whitespace-nowrap">Nur lesend</span>
                  )}
                  {canDelete && (
                    <AppButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDeleteRow(row)}
                      className={entityRowDeleteActionClassName}
                    >
                      Löschen
                    </AppButton>
                  )}
                </>
              )}
            </div>
          );
        },
      },
    ],
    [isDark, tableEditingId, tableDraft, savingId, canWrite, canDelete, startTableEdit, cancelTableEdit, saveTableEdit, decTable, incTable, handleDeleteRow]
  );

  /* Editing State für Mobile Cards */
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(0);

  const startEdit = useCallback((row) => {
    setEditingId(row.id);
    setDraft(Number(row.menge ?? 0));
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  const saveEdit = useCallback(
    async (row) => {
      await patchMenge(row, toMenge(draft));
      setEditingId(null);
    },
    [patchMenge, draft, toMenge]
  );

  const dec = useCallback(() => setDraft((v) => Math.max(0, Number((Number(v || 0) - 1).toFixed(2)))), []);
  const inc = useCallback(() => setDraft((v) => Math.max(0, Number((Number(v || 0) + 1).toFixed(2)))), []);

  const inventarCardActionCls =
    "!h-6 !min-h-6 !max-h-6 !px-1.5 !py-0 !text-[10px] !gap-1 leading-none whitespace-nowrap shrink-0 justify-center";

  /* Custom Card Renderer für Mobile mit Menge-Editing */
  const renderCard = useCallback(
    (row) => {
      const isEditing = editingId === row.id;
      const standortLabel = [row.standort_typ, row.standort_bez].filter(Boolean).join(" · ") || "—";
      const qtyBtnCls = cn(
        "!p-0 !text-[10px] !h-6 !min-h-6 !w-6 !min-w-6",
        isDark
          ? "!border-white/15 !bg-white/10 hover:!bg-white/15 !text-blue-100"
          : "!border-slate-300/50 !bg-white hover:!bg-slate-50 !text-slate-700 shadow-sm",
      );
      const stopCardNav = (e) => e.stopPropagation();

      return (
        <div
          className="app-frame rounded-lg border border-default hover:border-blue-400/30 transition-colors p-2 cursor-pointer interactive-card min-w-0"
          aria-label={`Artikel öffnen: ${row.bezeichnung || "Ohne Bezeichnung"}`}
        >
          <div className="min-w-0">
            <div className="flex items-start gap-1.5 min-w-0">
              <div className="min-w-0 flex-1">
                <h3 className="text-[13px] font-semibold leading-tight text-blue-100 truncate">
                  {row.bezeichnung || "—"}
                </h3>
                <p className="text-[10px] text-blue-200/60 truncate mt-0.5">
                  {row.projekttyp || "—"} · {row.kategorie || "—"}
                  {row.hersteller ? ` · ${row.hersteller}` : ""}
                </p>
              </div>
              <div className="shrink-0 pt-0.5">
                <InventarStatusBadge status={row.status || "verfügbar"} small />
              </div>
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-blue-200/65 min-w-0">
              <span className="truncate min-w-0" title={standortLabel}>
                {standortLabel}
              </span>
              {!isEditing ? (
                <>
                  <span className="text-blue-200/35" aria-hidden>
                    ·
                  </span>
                  <span className="tabular-nums font-medium text-blue-100 shrink-0">
                    Menge {Number(row.menge ?? 0)}
                  </span>
                </>
              ) : null}
            </div>

            {isEditing ? (
              <div
                className="mt-1 inline-flex items-center gap-0.5"
                onClick={stopCardNav}
                onKeyDown={stopCardNav}
                role="presentation"
              >
                <AppButton
                  type="button"
                  variant="secondary"
                  className={qtyBtnCls}
                  onClick={dec}
                  disabled={savingId === row.id}
                  aria-label="Menge verringern"
                >
                  −
                </AppButton>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  className="!w-12 !min-h-6 !h-6 !py-0 !px-1 !text-[10px] text-center"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onClick={stopCardNav}
                />
                <AppButton
                  type="button"
                  variant="secondary"
                  className={qtyBtnCls}
                  onClick={inc}
                  disabled={savingId === row.id}
                  aria-label="Menge erhöhen"
                >
                  +
                </AppButton>
              </div>
            ) : null}

            <div
              className="mt-1 pt-1 border-t border-white/10 flex flex-wrap items-center justify-end gap-0.5"
              onClick={stopCardNav}
              onKeyDown={stopCardNav}
              role="presentation"
            >
              {isEditing ? (
                <>
                  <AppButton
                    type="button"
                    variant="secondary"
                    onClick={() => saveEdit(row)}
                    className={inventarCardActionCls}
                  >
                    Speichern
                  </AppButton>
                  <AppButton
                    type="button"
                    variant="secondary"
                    onClick={cancelEdit}
                    className={inventarCardActionCls}
                  >
                    Abbrechen
                  </AppButton>
                </>
              ) : (
                <>
                  {canWrite ? (
                    <AppButton
                      type="button"
                      variant="secondary"
                      onClick={() => startEdit(row)}
                      className={inventarCardActionCls}
                      title="Menge bearbeiten"
                      aria-label="Menge bearbeiten"
                    >
                      Menge
                    </AppButton>
                  ) : (
                    <span className="text-[10px] text-blue-200/55 px-1">Nur lesend</span>
                  )}
                  {canDelete ? (
                    <AppButton
                      type="button"
                      variant="secondary"
                      onClick={() => handleDeleteRow(row)}
                      className={cn(inventarCardActionCls, destructiveTonalInteractiveClassName)}
                      aria-label="Löschen"
                    >
                      Löschen
                    </AppButton>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      );
    },
    [editingId, draft, savingId, canWrite, canDelete, isDark, startEdit, cancelEdit, saveEdit, dec, inc, handleDeleteRow]
  );

  const btnSec = isDark
    ? "px-4 py-2 rounded-lg text-sm border transition bg-white/10 hover:bg-white/15 text-blue-100 border-white/15 min-h-[44px]"
    : "px-4 py-2 rounded-lg text-sm border transition bg-white/80 hover:bg-white text-slate-700 border-slate-300/50 shadow-sm min-h-[44px]";

  const pagedData = sorted;

  const grad = "min-h-screen w-full bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const glass = "app-frame";
  const filterSortTrigger = cn(listSortTriggerClassName, "text-sm shrink-0");

  const renderInventarFilters = (mobile = false) => (
    <>
      {tab !== "Alle" && (
        <InventarKategorieFilter
          value={kat}
          onChange={setKat}
          options={kategorienOpts}
          triggerClassName={mobile ? cn(filterSortTrigger, "w-full min-h-[40px] justify-center") : filterSortTrigger}
        />
      )}
      <StatusFilter
        entity="generic"
        value={statusF}
        onChange={setStatusF}
        optionsOverride={INVENTAR_STATUS_OPTIONS}
        triggerClassName={mobile ? cn(filterSortTrigger, "w-full min-h-[40px] justify-center") : filterSortTrigger}
      />
      <SortButton
        value={sort}
        onChange={setSort}
        options={INVENTAR_SORT_OPTIONS}
        defaultDirFor={inventarSortDefaultDir}
        allowDirToggle={(key) => key !== "neueste" && key !== "menge"}
        className={mobile ? cn(filterSortTrigger, "w-full min-h-[40px] justify-center") : filterSortTrigger}
      />
    </>
  );

  return (
    <div className={cn(grad, "mobile-overflow-guard")}>
      <div className="mx-auto max-w-6xl px-3 sm:px-4 py-4 sm:py-6 min-w-0">
        <div className={cn("rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 mb-3 sm:mb-4 border", glass)}>
          <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">📦 Inventar</h1>
              <p className="mt-0.5 text-xs sm:text-sm text-blue-200/80" aria-live="polite">
                {listSummary}
              </p>
            </div>
            <div className={detailPageHeaderNavActionsClassName}>
              {canWrite && (
                <AppButton
                  type="button"
                  variant="primary"
                  size="lg"
                  onClick={() => navigate("/inventar/neu")}
                  className={`${detailPageHeaderNavTouchClassName} ${detailPageHeaderNavPrimaryMobileClassName}`}
                >
                  <span className="md:hidden">Neu</span>
                  <span className="hidden md:inline">Neuer Artikel</span>
                </AppButton>
              )}
              <AppButton type="button" variant="secondary" size="lg" onClick={() => navigate("/meine-aufgaben")} className={detailPageHeaderNavClassName}>
                Meine Aufgaben
              </AppButton>
              <AppButton type="button" variant="secondary" size="lg" onClick={() => smartBack(navigate, ["/", "/dashboard"])} className={detailPageHeaderNavClassName}>
                Zurück
              </AppButton>
              <AppButton type="button" variant="secondary" size="lg" onClick={() => navigate("/")} className={detailPageHeaderNavClassName}>
                Dashboard
              </AppButton>
            </div>
          </div>
        </div>

        <div className={cn("rounded-xl px-2 py-1.5 sm:px-2.5 sm:py-2 border mb-2 sm:mb-3", glass)}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[10px] uppercase tracking-wider text-blue-200/45 shrink-0 w-[4.25rem]">Bereich</span>
            <div className="flex flex-wrap items-center gap-1 min-w-0">
              {TYPEN.map((t) => {
                const active = tab === t;
                const emoji = t === "Alle" ? null : emojiForProjektTyp(String(t).toLowerCase());
                return (
                  <AppButton
                    type="button"
                    key={t}
                    variant="ghost"
                    onClick={() => setTab(t)}
                    className={cn(
                      segmentTabChipButtonClassName,
                      "gap-1",
                      active ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"
                    )}
                    aria-pressed={active}
                  >
                    {emoji ? <span aria-hidden>{emoji}</span> : null}
                    <span>{tabLabel(t)}</span>
                  </AppButton>
                );
              })}
            </div>
          </div>
        </div>

        <div className={cn("rounded-xl px-2 py-1.5 sm:px-2.5 sm:py-2 border mb-3 sm:mb-4", glass)}>
          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:gap-2">
            <div className="relative w-full min-w-0 xl:flex-1 xl:min-w-[20rem]">
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suche…"
                title="Bezeichnung, Typ, Kategorie, Status, Hersteller, SN, Standort, ID"
                className={cn("px-3 py-2 min-h-[40px] w-full text-[16px] xl:text-sm", search ? "pr-12" : "pr-3")}
                aria-label="Inventar suchen"
              />
              {search ? (
                <AppButton
                  type="button"
                  variant="ghost"
                  onClick={() => setSearch("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 !min-h-[36px] !min-w-[36px] !p-0 !border-0 !bg-transparent !shadow-none text-blue-200/70 hover:!bg-transparent hover:text-white"
                  aria-label="Suche löschen"
                  title="Suche löschen"
                >
                  ✕
                </AppButton>
              ) : null}
            </div>
            {isDesktopXl ? (
              <div className="flex flex-wrap items-center gap-2 min-w-0 xl:ml-auto xl:flex-nowrap xl:justify-end">
                {renderInventarFilters(false)}
              </div>
            ) : (
              <FilterBarOrDrawer title="Filter & Sortierung" drawerUntil="xl" className="w-full justify-center">
                {renderInventarFilters(true)}
              </FilterBarOrDrawer>
            )}
          </div>
        </div>

        <div className={cn("rounded-xl border overflow-hidden mb-3 sm:mb-6", glass)}>
          <ResponsiveDataView
            data={pagedData}
            columns={columns}
            renderCard={renderCard}
            onRowClick={(row) => navigate(`/inventar/${row.id}`)}
            emptyMessage={
              items.length === 0
                ? "Noch keine Inventarartikel — jetzt erstellen."
                : "Keine Einträge für diese Filter gefunden."
            }
            loading={loading}
            className="p-1.5 sm:p-2 !space-y-1.5"
          />
        </div>

        {!loading && !err && paginationFooter}

        {/* Error State */}
        {!loading && err && (
          <div
            className={`rounded-2xl px-4 py-4 border ${
              isDark ? "glass border-white/10 text-red-300" : "bg-white/80 border-slate-300/50 text-red-600 shadow-sm"
            }`}
          >
            {err}
            <div className="mt-3">
              <AppButton type="button" onClick={refetch} variant="secondary">Erneut versuchen</AppButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


